/**
 * Shared hook to create (or reuse) a recommended source collection
 * and activate it in source control.
 *
 * Used by the Evidence Prompts Bar and Evidence Summary Suggestion Banner
 * so that clicking a suggestion activates curated sources matched to the
 * user's specialty and region.
 */

import { sourceControlToggleAtom } from '@/atoms/sourceControlAtoms';
import { IS_UNIFIED_COLLECTIONS_ENABLED } from '@/components/constants/flags';
import { useQuerySourceRecommendations } from '@/hooks/query/useQuerySourceRecommendations';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { logger } from '@/utils/logger/logger';
import { getMaxDomainsPerCollection } from '@heidi/evidence/contracts';
import {
  useCollections,
  useCreateCollection,
  useCreateSourceList,
  useSourceLists,
} from '@heidi/evidence/data';
import { getCountryCodeByName } from '@heidi/utils/countries';
import { useSetAtom } from 'jotai';
import { useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
export function useActivateRecommendedSources() {
  const intl = useIntl();
  const { data: recommendationsData } = useQuerySourceRecommendations();
  const { mutateAsync: createSourceList } = useCreateSourceList();
  const { mutateAsync: createCollection } = useCreateCollection();
  const { data: existingSourceLists } = useSourceLists();
  const { data: existingCollections } = useCollections();
  const setSourceControlToggle = useSetAtom(sourceControlToggleAtom);

  const { data: userData } = useQueryUser((data) => ({
    specialtyId: data?.specialty_id,
  }));
  const { data: specialtiesData } = useQuerySpecialties();

  const specialtyName =
    specialtiesData?.specialties.find((s) => s.id === userData?.specialtyId)
      ?.localizedName ?? '';

  const matchedCountryName = recommendationsData?.matched_country ?? '';
  const safeCountryCode = (
    getCountryCodeByName(matchedCountryName) ?? matchedCountryName
  ).trim();

  const collectionName =
    specialtyName && safeCountryCode
      ? intl.formatMessage(
          { defaultMessage: '{specialty} - {countryCode}', id: 'qu5ct86XXl' },
          { specialty: specialtyName, countryCode: safeCountryCode }
        )
      : specialtyName || safeCountryCode;

  const isActivatingRef = useRef(false);

  const activateRecommendedSources = useCallback(async (): Promise<string | null> => {
    const sources = recommendationsData?.sources ?? [];
    if (sources.length === 0 || isActivatingRef.current || !collectionName) return null;

    try {
      isActivatingRef.current = true;
      const name = collectionName;
      // Cap to the BE-enforced ceiling (100 legacy / 300 unified). The BE
      // recommendation payload is ordered anchors → specialty → country,
      // so slicing keeps the highest-quality sources and only sheds the
      // long-tail country-matched fillers. Silent by design — the banner
      // never surfaced the total count to the user (only a 4-favicon
      // preview), so there's no expectation gap.
      // Both flag readers are evaluated inside the callback so they pick
      // up mid-session flag flips instead of latching the value captured
      // at hook-mount time.
      const isUnified = IS_UNIFIED_COLLECTIONS_ENABLED();
      const cappedDomains = sources
        .map((s) => s.domain)
        .slice(0, getMaxDomainsPerCollection(isUnified));

      let collectionId: string;

      if (isUnified) {
        const existing = existingCollections?.collections.find((c) => c.name === name);
        collectionId = existing
          ? existing.collection_id
          : (await createCollection({ name, domains: cappedDomains })).collection_id;
      } else {
        const existing = existingSourceLists?.find((l) => l.name === name);
        collectionId = existing
          ? existing.id
          : (await createSourceList({ name, domains: cappedDomains })).id;
      }

      setSourceControlToggle((prev) => ({
        ...prev,
        webCollections: true,
        webCollectionsMode: 'collections' as const,
        selectedWebCollectionIds: [collectionId],
      }));

      return collectionId;
    } catch (error) {
      logger.error('[Evidence] Failed to activate recommended source collection', error);
      return null;
    } finally {
      isActivatingRef.current = false;
    }
  }, [
    recommendationsData,
    existingSourceLists,
    existingCollections,
    createSourceList,
    createCollection,
    setSourceControlToggle,
    collectionName,
  ]);

  return { activateRecommendedSources };
}
